import dayjs from "dayjs";
import { useMemo } from "react";

import { LineChart } from "@design-system/LineChart/LineChart";

import { useEmployeeApplicationUsageLineChartQuery } from "./ClientEmployeeAppUsageLineChart.g";

type Props = {
  employeeId: string;
  daysToShow?: number;
  startDate?: Date;
  endDate?: Date;
  parentCategoryId?: string;
};

export const ClientEmployeeAppUsageLineChart = ({
  employeeId,
  daysToShow = 30,
  startDate,
  endDate,
  parentCategoryId,
}: Props) => {
  const { chartStartDate, chartEndDate } = useMemo(() => {
    if (startDate && endDate) {
      return {
        chartStartDate: dayjs(startDate),
        chartEndDate: dayjs(endDate),
      };
    }
    const end = dayjs();
    const start = end.subtract(daysToShow, "day");
    return {
      chartStartDate: start,
      chartEndDate: end,
    };
  }, [daysToShow, startDate, endDate]);

  const applicationEmployeeUsageLineChartQuery =
    useEmployeeApplicationUsageLineChartQuery({
      employeeId,
      dateFrom: chartStartDate.format("YYYY-MM-DD"),
      dateTo: chartEndDate.format("YYYY-MM-DD"),
      ...(parentCategoryId && { categoryIds: [parentCategoryId] }),
    });

  const applicationUsageByDate = useMemo(() => {
    const dailySummary =
      applicationEmployeeUsageLineChartQuery.data
        ?.clientEmployeeApplicationInteractions?.dailySummary;

    if (!dailySummary || dailySummary.length === 0) {
      return [];
    }

    // Convert dailySummary to the format expected by the chart
    return dailySummary.map((summary) => ({
      date: dayjs(summary.date).format("DD/MM/YYYY"),
      resourceGroupCount: summary.uniqueResourceGroups,
    }));
  }, [applicationEmployeeUsageLineChartQuery.data]);

  const data = useMemo(() => {
    const actualDaysToShow = Math.max(
      1,
      chartEndDate.diff(chartStartDate, "day") + 1,
    );

    // Create a map to store resource group counts per date
    const resourceGroupCountsByDate = new Map<string, number>();

    applicationUsageByDate?.forEach((usage) => {
      if (usage.date && usage.resourceGroupCount !== undefined) {
        resourceGroupCountsByDate.set(usage.date, usage.resourceGroupCount);
      }
    });

    // Generate data for all dates in our range
    const chartData = Array.from({ length: actualDaysToShow }, (_, i) => {
      const currentDate = chartEndDate.subtract(actualDaysToShow - 1 - i, "day");
      const dateStr = currentDate.format("DD/MM/YYYY");
      const resourceGroupCount = resourceGroupCountsByDate.get(dateStr) || 0;

      return {
        x: dateStr,
        y: resourceGroupCount,
      };
    });

    // Separately calculate weekend dates for highlighting
    const weekendDates = Array.from({ length: actualDaysToShow }, (_, i) => {
      const currentDate = chartEndDate.subtract(actualDaysToShow - 1 - i, "day");
      const isWeekend = currentDate.day() === 0 || currentDate.day() === 6; // Sunday = 0, Saturday = 6
      return isWeekend ? currentDate.format("DD/MM/YYYY") : null;
    }).filter(Boolean) as string[];

    return { chartData, weekendDates };
  }, [applicationUsageByDate, chartEndDate, chartStartDate]);

  // Force chart re-render when date range changes to prevent artifacts
  const chartKey = useMemo(() => {
    return `${chartStartDate.format("YYYY-MM-DD")}-${chartEndDate.format("YYYY-MM-DD")}-${parentCategoryId || "all"}`;
  }, [chartStartDate, chartEndDate, parentCategoryId]);

  return (
    <LineChart
      key={chartKey}
      data={[
        {
          id: "resource-group-usage",
          color: "red",
          data: data.chartData,
        },
      ]}
      weekendDates={data.weekendDates}
      xAxisName=""
      yAxisName="Resource Groups"
      tooltipLabel="Resource Groups"
    />
  );
};
